import {
  AfterViewInit,
  Component,
  ElementRef, EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import { CategoryScale, Chart, ChartDataset, ChartItem } from 'chart.js';
import { COLORS } from "../../configs";
import { tickValueToAxisLabel } from "./helpers";
import { SimpleMatTooltipComponent } from "../../../simple-mat-tooltip";
import { abbreviateNumber, calcAutoFontSize } from "../../../../../helpers";
import { MAP_DEVICE_PIXEL_RATIO, TOOLTIP_AUTO_HIDE_TIMEOUT } from "../../configs";
import {
  axisLabelDrawCallbackPlugin,
  BarChartAxisLabelData,
  axisLabelEventPlugin,
  defaultOnLabelMouseMove,
} from '../../plugins';
import ChartDataLabels from 'chartjs-plugin-datalabels';

@Component({
    selector: 'app-bar-chart',
    templateUrl: './bar-chart.component.html',
    styleUrls: ['bar-chart.component.scss'],
    standalone: false
})
export class BarChartComponent implements AfterViewInit, OnDestroy {

  @Output() xAxisLabelData = new EventEmitter<BarChartAxisLabelData[]>();
  @Output() yAxisLabelData = new EventEmitter<BarChartAxisLabelData[]>();

  @Input()
  set xPostfix(data: string) {
    this._xPostfix = data;
    this.createChart();
  };

  @Input()
  set yPostfix(data: string) {
    this._yPostfix = data;
    this.createChart();
  };

  @Input()
  set autoAxisLabelFontSize(data: boolean) {
    this._autoAxisLabelFontSize = data;
    this.createChart();
  };

  @Input()
  set axisLabelFontSize(data: number) {
    this._axisLabelFontSize = data;
    this.createChart();
  };

  @Input()
  set paddingLeft(data: number) {
    this._paddingLeft = data;
    this.createChart();
  };

  @Input()
  set isVertical(data: boolean) {
    this._isVertical = data;
    this.createChart();
  };

  @Input()
  set showDataLabel(data: boolean) {
    this._showDataLabel = data;
    this.createChart();
  };

  @Input()
  set showHorizontalGridLines(data: boolean) {
    this._showHorizontalGridLines = data;
    this.createChart();
  };

  @Input()
  set showTooltips(data: boolean) {
    this._showTooltips = data;
    this.createChart();
  };

  @Input()
  set chartLabels(data: string[]) {
    this._chartLabels = data;
    this.createChart();
  };

  @Input()
  set chartData(data: number[]) {
    this._chartData = data;
    this.createChart();
  };

  @Input()
  set chartDataSet(data: ChartDataset<'bar'>[]) {
    this._chartDataSet = data;    
    this.createChart();
  }

  @Input()
  set stacked(data: boolean) {
    this._stacked = data;
    this.createChart();
  };

  @Input()
  set useAbbreviationXAxis(value: boolean) {
    this._useAbbreviationXAxis = value;
    this.createChart();
  };

  @Input()
  set useAbbreviationYAxis(value: boolean) {
    this._useAbbreviationYAxis = value;
    this.createChart();
  };
  

  @ViewChild('canvas', {static: true}) barChartCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('tooltip', {static: true}) tooltip: SimpleMatTooltipComponent;

  readonly tooltipAutoHideTimeout = TOOLTIP_AUTO_HIDE_TIMEOUT;

  private _chart: Chart = null;

  private _chartLabels: string[];

  private _chartData: number[];

  private _chartDataSet: ChartDataset<'bar'>[];

  private _isVertical: boolean = true;

  private _paddingLeft: number = 0;

  private _axisLabelFontSize: number = 10.6;

  private _autoAxisLabelFontSize: boolean = false;

  private _xPostfix: string = '';

  private _yPostfix: string = '';

  private _showDataLabel: boolean = false;

  private _showHorizontalGridLines: boolean = false;

  private _showTooltips: boolean = true;

  private _stacked: boolean = false;
  
  private _useAbbreviationXAxis: boolean = false;

  private _useAbbreviationYAxis: boolean = false;

  private createChart(): void {
    if (!this.barChartCanvas || !this._chartLabels || (!this._chartData && !this._chartDataSet)) {
      return
    }

    this.destroyChart();

    const context: ChartItem = this.barChartCanvas.nativeElement.getContext('2d');
    const tooltip: SimpleMatTooltipComponent = this.tooltip;
    const yAxisLabelData = this.yAxisLabelData;
    const xAxisLabelData = this.xAxisLabelData;
    const axisLabelFontSize = this._axisLabelFontSize;
    const calcFontSize = this._autoAxisLabelFontSize ? () => calcAutoFontSize() : () => axisLabelFontSize;
    const xPostfix = this._xPostfix || '';
    const yPostfix = this._yPostfix || '';
    const isAllZero = Array.isArray(this._chartData) && this._chartData.length > 0
        ? this._chartData.every(val => val === 0)
        : false;
    const useAbbreviationXAxis = this._useAbbreviationXAxis;
    const useAbbreviationYAxis = this._useAbbreviationYAxis;

    if (context) {
      this._chart = new Chart(context,
        {
          type: 'bar',
          plugins: [
            axisLabelEventPlugin,
            axisLabelDrawCallbackPlugin,
            ...(this._showDataLabel || this._stacked ? [ChartDataLabels] : []),
          ],
          data: {
            labels: this._chartLabels,
            datasets: this._stacked ? this._chartDataSet :  [{
              data: this._chartData,
              backgroundColor: COLORS.BLUE_600,
              hoverBackgroundColor: COLORS.BLUE_700,
            }]
          },
          options: {
            devicePixelRatio: MAP_DEVICE_PIXEL_RATIO,
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: this._isVertical ? 'x' : 'y',
            layout: {
              padding: {
                left: this._paddingLeft,
                top: this._showDataLabel ? 20 : 0,
              }
            },
            scales: {
              y: {
                stacked: this._stacked,
                grid: {
                  display: this._showHorizontalGridLines,
                },
                beginAtZero: true,
                max: this._stacked ? this.getMax() : null,
                ticks: {
                  maxRotation: 0,
                  minRotation: 0,
                  font: {
                    size: calcFontSize
                  },
                  stepSize: isAllZero ? 0.1 : null,
                  callback: function (value) {
                    if (Number.isInteger(value)) {
                      return tickValueToAxisLabel(
                        this as CategoryScale,
                        value,
                        yPostfix,
                        useAbbreviationYAxis
                      );
                    }
                    return '';
                  },
                }
              },
              x: {
                stacked: this._stacked,
                grid: {
                  display: false,
                },
                ticks: {
                  maxRotation: 0,
                  minRotation: 0,
                  font: {
                    size: calcFontSize
                  },
                  callback: function (value) {
                    return tickValueToAxisLabel(this as CategoryScale, value, xPostfix, useAbbreviationXAxis);
                  }
                }
              }
            },
            plugins: {
              [axisLabelEventPlugin.id]: {
                xPostfix,
                yPostfix,
                onLabelMouseMove: this._showTooltips ? defaultOnLabelMouseMove(tooltip) : () => {tooltip.show = false;}
              },
              [axisLabelDrawCallbackPlugin.id]: {
                onYDrawCallback: (data: BarChartAxisLabelData[]) => {
                  yAxisLabelData.emit(data);
                },
                onXDrawCallback: (data: BarChartAxisLabelData[]) => {
                  xAxisLabelData.emit(data);
                }
              },
              legend: {
                display: this._stacked,
                labels: {
                    usePointStyle: false,
                    boxWidth: 8,
                    boxHeight: 4 ,
                    font: { weight: 'bold' },
                }
              },
              tooltip: {
                enabled: this._stacked,
              },
              datalabels: this._showDataLabel || this._stacked ? {
                color: COLORS.BLUE_600,
                anchor: 'end',
                offset: -20,
                align: 'start',
                formatter: (value, context) => {
                    if (this._stacked) {
                    const { datasetIndex, dataIndex, chart } = context;
                    const datasets = chart.data.datasets;

                    let total = 0;
                    let lastVisibleDatasetIndex = -1;

                    datasets.forEach((ds, idx) => {
                        const meta = chart.getDatasetMeta(idx);
                        if (!meta.hidden) { 
                        const v = ds.data[dataIndex];
                        if (typeof v === 'number') {
                            total += v;
                        }
                        lastVisibleDatasetIndex = idx;
                        }
                    });

                    if (datasetIndex === lastVisibleDatasetIndex) {
                        return total > 0 ? abbreviateNumber(total) : '';
                    }
                    return '';
                    }
                    return value > 0 ? abbreviateNumber(value) : '';
                }} : { display: false },
            }
          }
        }
      )
    }
  }

  ngAfterViewInit(): void {
    if (!this._chart) {
      this.createChart();
    }
  }

  ngOnDestroy(): void {
    this.destroyChart();
  }

  private destroyChart() {
    if (this._chart) {
      this._chart.destroy();
      this._chart = null;
    }
  }

private getMax(): number {
    if (!this._chartDataSet || this._chartDataSet.length === 0) {
        return 1;
    }

    const dataLength = this._chartDataSet[0].data.length;

    const summedValues = Array(dataLength).fill(0);

    this._chartDataSet.forEach(dataset => {
        dataset.data.forEach((value, index) => {
        if (typeof value === 'number') {
            summedValues[index] += value;
        }
        });
    });

    const maxValue = summedValues.length > 0 ? Math.max(...summedValues) : 0;

    if (maxValue === 0) return 1;

    const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
    const stepSize = magnitude;
    const roundedMax = Math.ceil(maxValue / stepSize) * stepSize;

    return roundedMax + stepSize;
}

}

